import {useEffect, useState, useCallback, useRef, useId} from 'react'
import {InputRange} from 'dom-input-range'
import {ActionList} from '@primer/react'
import type {ChronicleCommand} from '../../hooks/use-chronicle-autocomplete'
import styles from './ChronicleAutocomplete.module.css'

function getCaretPosition(textarea: HTMLTextAreaElement, currentText: string) {
  const textareaRect = textarea.getBoundingClientRect()
  if (textarea.getClientRects().length === 0 || textareaRect.width === 0 || textareaRect.height === 0) return null

  try {
    const selStart = textarea.selectionStart ?? currentText.length
    const rangeRect = new InputRange(textarea, selStart).getBoundingClientRect()
    const isWithinTextarea =
      rangeRect.left >= textareaRect.left - 1 &&
      rangeRect.left <= textareaRect.right + 1 &&
      rangeRect.top >= textareaRect.top - 1 &&
      rangeRect.bottom <= textareaRect.bottom + 1

    if (isWithinTextarea) {
      return {top: rangeRect.bottom, left: rangeRect.left}
    }
  } catch {
    // Fall back to the textarea position below when caret measurement is unavailable.
  }

  return {top: textareaRect.top + 24, left: textareaRect.left + 8}
}

interface ChronicleAutocompleteProps {
  suggestions: ChronicleCommand[]
  onSelect: (command: string) => void
  onDismiss?: () => void
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  currentText: string
}

/**
 * Dropdown that shows matching chronicle commands as the user types.
 * Positioned right below the caret using dom-input-range.
 * Supports keyboard navigation (ArrowUp/Down, Enter, Escape).
 */
export function ChronicleAutocomplete({
  suggestions,
  onSelect,
  onDismiss,
  textAreaRef,
  currentText,
}: ChronicleAutocompleteProps) {
  const [caretPos, setCaretPos] = useState<{top: number; left: number} | null>(null)
  const positionKey = `${currentText}\u0000${suggestions.map(command => command.key).join('\u0000')}`
  const [positionedKey, setPositionedKey] = useState<string | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [hasNavigated, setHasNavigated] = useState(false)
  const instanceId = useId()
  const listboxId = `chronicle-autocomplete-listbox-${instanceId}`
  const hasSuggestions = suggestions.length > 0

  // Invalidate coordinates from a previous text or suggestion list before they
  // can be reused if the same query becomes active again.
  if (positionedKey !== null && positionedKey !== positionKey) {
    setPositionedKey(null)
  }

  // Avoid painting persisted suggestions at the viewport origin before measuring the caret.
  const isPositioned = hasSuggestions && caretPos !== null && positionedKey === positionKey

  // Clamp active index to valid range when suggestions shrink
  const clampedIndex = suggestions.length > 0 ? activeIndex % suggestions.length : 0
  const clampedIndexRef = useRef(clampedIndex)

  // Keep ref in sync and set aria-activedescendant for screen readers
  useEffect(() => {
    clampedIndexRef.current = clampedIndex
    const textarea = textAreaRef.current
    if (!textarea || !isPositioned) return

    const activeOptionId = suggestions[clampedIndex] ? `chronicle-option-${instanceId}-${clampedIndex}` : ''
    textarea.setAttribute('aria-activedescendant', activeOptionId)

    return () => {
      textarea.removeAttribute('aria-activedescendant')
    }
  }, [suggestions, clampedIndex, isPositioned, textAreaRef, instanceId])

  // Reset navigation state when suggestions change
  const [prevSuggestionsLength, setPrevSuggestionsLength] = useState(suggestions.length)
  if (prevSuggestionsLength !== suggestions.length) {
    setPrevSuggestionsLength(suggestions.length)
    setHasNavigated(false)
    setActiveIndex(0)
  }

  useEffect(() => {
    if (!hasSuggestions) return

    let firstFrame: number | null = null
    let secondFrame: number | null = null
    let retryFrame: number | null = null
    let retryCount = 0

    const measure = () => {
      const textarea = textAreaRef.current
      const nextCaretPos = textarea ? getCaretPosition(textarea, currentText) : null

      if (!nextCaretPos) {
        setPositionedKey(previousKey => (previousKey === positionKey ? null : previousKey))
        if (retryCount < 5) {
          retryCount += 1
          retryFrame = requestAnimationFrame(measure)
        }
        return
      }

      setPositionedKey(previousKey => (previousKey === positionKey ? previousKey : positionKey))
      setCaretPos(previousPosition => {
        if (previousPosition?.top === nextCaretPos.top && previousPosition.left === nextCaretPos.left) {
          return previousPosition
        }
        return nextCaretPos
      })
    }

    const scheduleMeasure = () => {
      if (firstFrame !== null) cancelAnimationFrame(firstFrame)
      if (secondFrame !== null) cancelAnimationFrame(secondFrame)
      if (retryFrame !== null) cancelAnimationFrame(retryFrame)
      retryCount = 0

      // Wait for the main menu portal and its positioning to settle.
      firstFrame = requestAnimationFrame(() => {
        secondFrame = requestAnimationFrame(measure)
      })
    }

    const textarea = textAreaRef.current
    const styleClone = textarea ? new InputRange(textarea).getStyleClone() : null
    styleClone?.addEventListener('update', scheduleMeasure)
    scheduleMeasure()

    return () => {
      if (firstFrame !== null) cancelAnimationFrame(firstFrame)
      if (secondFrame !== null) cancelAnimationFrame(secondFrame)
      if (retryFrame !== null) cancelAnimationFrame(retryFrame)
      styleClone?.removeEventListener('update', scheduleMeasure)
    }
  })

  const handleSelect = useCallback(
    (command: string) => {
      onSelect(command)
      textAreaRef.current?.focus()
    },
    [onSelect, textAreaRef],
  )

  // Keyboard navigation
  const onDismissRef = useRef(onDismiss)

  useEffect(() => {
    onDismissRef.current = onDismiss
  }, [onDismiss])

  useEffect(() => {
    if (!isPositioned) return
    const textarea = textAreaRef.current
    if (!textarea) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHasNavigated(true)
          setActiveIndex(i => (i + 1) % suggestions.length)
          break
        case 'ArrowUp':
          e.preventDefault()
          setHasNavigated(true)
          setActiveIndex(i => (i - 1 + suggestions.length) % suggestions.length)
          break
        case 'Enter': {
          if (!hasNavigated) break
          e.preventDefault()
          e.stopPropagation()
          const cmd = suggestions[clampedIndexRef.current]
          if (cmd) handleSelect(cmd.key)
          break
        }
        case 'Tab': {
          // Only intercept Tab if user has explicitly navigated with arrows
          if (!hasNavigated) break
          e.preventDefault()
          e.stopPropagation()
          const cmd = suggestions[clampedIndexRef.current]
          if (cmd) handleSelect(cmd.key)
          break
        }
        case 'Escape':
          e.preventDefault()
          e.stopPropagation()
          onDismissRef.current?.()
          break
      }
    }

    textarea.addEventListener('keydown', handleKeyDown)
    return () => textarea.removeEventListener('keydown', handleKeyDown)
  }, [suggestions, handleSelect, isPositioned, textAreaRef, hasNavigated])

  if (!isPositioned) return null

  const dropdownTop = caretPos.top + 4

  return (
    <div
      className={styles.dropdown}
      style={{
        '--dropdown-top': `${dropdownTop}px`,
        top: `${dropdownTop}px`,
        left: `${caretPos.left}px`,
      }}
    >
      <ActionList
        id={listboxId}
        aria-label="Chronicle command suggestions"
        role="listbox"
        variant="inset"
        disableFocusZone
      >
        {suggestions.map((cmd, index) => (
          <ActionList.Item
            key={cmd.key}
            id={`chronicle-option-${instanceId}-${index}`}
            role="option"
            active={index === clampedIndex}
            aria-selected={index === clampedIndex}
            tabIndex={-1}
            onSelect={() => handleSelect(cmd.key)}
          >
            <span className={styles.commandLabel}>{cmd.key}</span>
            <ActionList.Description variant="inline" className={styles.commandDescription}>
              {cmd.description}
            </ActionList.Description>
          </ActionList.Item>
        ))}
      </ActionList>
    </div>
  )
}
